import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import addPaymentSchema from '@/schemas/add-payment';
import { TPaymentType } from '@/services/paymentType';
import { capitalizeFirstLetter, currencyFormat } from '@/utils/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AddDiscountModalT, TForm } from './add-discount-modal.types';
const AddDiscountModal = ({
  open,
  onOpenChange,
  onSave,
  sale,
  paymentSelected,
  paymentType,
  loading,
  organization
}: AddDiscountModalT) => {
  const form = useForm({
    resolver: zodResolver(
      addPaymentSchema(sale.amountPending, paymentSelected?.amount)
    ),
    defaultValues: {
      user: `${capitalizeFirstLetter(sale.user.firstName)} ${capitalizeFirstLetter(sale.user.lastName)}`,
      service: sale.service.name ?? '',
      amount: paymentSelected?.amount ? paymentSelected.amount : 0,
      paymentMethod: (
        paymentType.find((method) => method.name === 'Discount') as TPaymentType
      ).id,
      amountPending: currencyFormat(sale.amountPending, organization.currency),
      total: '',
      comments: paymentSelected?.observations ?? '',
      date: new Date()
    }
  });
  const { t } = useTranslation();
  function onSubmit(values: z.infer<TForm>) {
    const formValues = {
      paymentType: values.paymentMethod,
      sale: sale.id,
      amount: Number(values.amount),
      observations: values.comments
        ? 'Descuento -' + values.comments
        : 'Descuento',
      paidAt: values.date.getTime()
    };

    onSave(formValues);
  }

  useEffect(() => {
    form.setValue(
      'amountPending',
      currencyFormat(sale.amountPending, organization.currency)
    );
  }, [sale]);

  return (
    <Dialog
      open={open}
      onOpenChange={(openState) => {
        onOpenChange(openState);
        if (!openState) form.reset();
      }}
    >
      <DialogContent className="overflow-y-scroll h-5/6">
        <DialogHeader>
          <DialogTitle>{t('modals.addDiscount.title')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name={'user'}
              disabled
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>{t('modals.addPayment.user')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name={'service'}
              disabled
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>
                      {t('modals.addPayment.serviceContracted')}
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name={'amountPending'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>
                      {t('modals.addPayment.amountPending')}
                    </FormLabel>
                    <FormControl>
                      <Input readOnly {...field} disabled />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name={'amount'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>{t('modals.addDiscount.discount')}</FormLabel>
                    <FormDescription>
                      {t('modals.addDiscount.discountDecription')}
                    </FormDescription>
                    <FormControl>
                      <Input {...field} type="number" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <FormField
              control={form.control}
              name={'comments'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormLabel>{t('modals.addPayment.comments')}</FormLabel>
                    <FormControl>
                      <Textarea className="resize-none" {...field} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <Loader className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                t('common.save')
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddDiscountModal;
